'use client'

import Image from 'next/image'
import { RichText } from '@payloadcms/richtext-lexical/react'
import { Card, CardHeader, CardDescription, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import type { Bio, Media } from '@/payload-types'

type BiosLexicalClientProps = {
  bios: Bio[]
}

function getObjectPosition(mediaDoc: Media): string | undefined {
  const focalX = typeof mediaDoc.focalX === 'number' ? mediaDoc.focalX : 50
  const focalY = typeof mediaDoc.focalY === 'number' ? mediaDoc.focalY : 50

  if (typeof mediaDoc.focalX !== 'number' && typeof mediaDoc.focalY !== 'number') {
    return undefined
  }

  const clampedX = Math.min(100, Math.max(0, focalX))
  const clampedY = Math.min(100, Math.max(0, focalY))

  return `${clampedX}% ${clampedY}%`
}

function getBioImage(media: Bio['image']): { url: string; width?: number; height?: number; alt: string; objectPosition?: string } | null {
  if (!media || typeof media === 'string') return null

  const mediaDoc = media as Media
  const size = mediaDoc.sizes?.medium || mediaDoc.sizes?.large
  const url = size?.url || mediaDoc.url
  if (!url) return null

  return {
    url,
    width: size?.width || mediaDoc.width || undefined,
    height: size?.height || mediaDoc.height || undefined,
    alt: mediaDoc.alt || '',
    objectPosition: getObjectPosition(mediaDoc),
  }
}

export function BiosLexicalClient({ bios }: BiosLexicalClientProps) {
  if (!bios.length) return null

  return (
    <section className="w-full">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {bios.map((bio) => {
          const image = getBioImage(bio.image)

          return (
            <Dialog key={bio.id}>
              <DialogTrigger asChild>
                <button type="button" className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Card className="theme-bio h-full cursor-pointer">
                    {image ? (
                      <div className="relative aspect-square w-full">
                        <Image
                          src={image.url}
                          alt={image.alt || bio.name}
                          fill
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          className="object-cover"
                          style={{ objectPosition: image.objectPosition }}
                        />
                      </div>
                    ) : null}
                    <CardHeader className={"flex-1 flex flex-col"}>
                      <CardTitle>{bio.name}</CardTitle>
                      {bio.title ? <CardDescription className={"mb-auto"}>{bio.title}</CardDescription> : null}
                    </CardHeader>
                  </Card>
                </button>
              </DialogTrigger>

              <DialogContent className="flex max-h-[90vh] w-[min(94vw,64rem)] flex-col overflow-hidden p-0 sm:max-w-4xl">
                <DialogHeader>
                  <DialogTitle>{bio.name}</DialogTitle>
                  {bio.title ? <DialogDescription>{bio.title}</DialogDescription> : null}
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
                  {image ? (
                    <div className="relative mb-4 aspect-[16/9] w-full overflow-hidden rounded-md border">
                      <Image
                        src={image.url}
                        alt={image.alt || bio.name}
                        fill
                        sizes="(max-width: 768px) 94vw, 1024px"
                        className="object-cover"
                        style={{ objectPosition: image.objectPosition }}
                      />
                    </div>
                  ) : null}

                  {bio.bio ? (
                    <RichText
                      data={bio.bio}
                      className="flex flex-col content-block-text w-full gap-theme-md md:gap-theme-lg"
                    />
                  ) : null}
                </div>
              </DialogContent>
            </Dialog>
          )
        })}
      </div>
    </section>
  )
}
